import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Card } from '@blueprintjs/core';
import type { BenefitCondition } from '../../types';
import './LivingBenefitsPage.scss';

const benefitConditions: BenefitCondition[] = [
  {
    id: '1',
    category: 'critical-illness',
    title: 'Critical Illness',
    description: 'Access your death benefit early if diagnosed with a major critical illness.',
    examples: ['Cancer (invasive)', 'Heart Attack', 'Stroke', 'Major Organ Transplant', 'Kidney Failure', 'ALS'],
  },
  {
    id: '2',
    category: 'chronic',
    title: 'Chronic Illness',
    description: 'Receive benefits if you become unable to perform activities of daily living.',
    examples: [
      'Unable to perform 2 of 6 ADLs (bathing, dressing, toileting, transferring, continence, eating)',
      'Severe cognitive impairment requiring substantial supervision',
    ],
  },
  {
    id: '3',
    category: 'critical-injury',
    title: 'Critical Injury',
    description: 'Protection for severe injuries that significantly impact your life.',
    examples: ['Paralysis (quadriplegia, paraplegia)', 'Coma', 'Major Burns (severe burns covering significant body area)', 'Traumatic Brain Injury'],
  },
];

const LivingBenefitsPage: React.FC = () => {
  return (
    <div className="living-benefits-page">
      {/* Hero Section */}
      <section className="living-benefits-page__hero section--gradient">
        <div className="container">
          <div className="living-benefits-page__hero-content">
            <span className="living-benefits-page__eyebrow">The New Standard</span>
            <h1>Living Benefits Explained</h1>
            <p>
              Life insurance that does more than just pay when you die. Living Benefits
              give you access to your death benefit while you're still alive — when facing
              serious illness or injury.
            </p>
          </div>
        </div>
      </section>

      {/* What Are Living Benefits */}
      <section className="section">
        <div className="container">
          <div className="living-benefits-page__intro">
            <h2>What Are Living Benefits?</h2>
            <p className="living-benefits-page__intro-lead">
              Living Benefits life insurance represents a modern evolution in life insurance coverage.
              Unlike traditional policies that only pay out upon death, Living Benefits policies include
              <strong> Accelerated Benefit Riders (ABRs)</strong> that grant early access to your death
              benefit if you experience certain qualifying health events.
            </p>
            <div className="living-benefits-page__highlight">
              <Icon icon="info-sign" size={24} />
              <p>
                <strong>The best part?</strong> Top Living Benefits products include these riders at
                <strong> no extra premium cost</strong>. You get more protection for the same price
                as traditional life insurance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <h2>How Living Benefits Work</h2>
            <p className="living-benefits-page__section-subtitle">
              A simple process to access your benefits when you need them most.
            </p>
          </div>
          <div className="living-benefits-page__steps">
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">1</div>
              <h3>Get Protected</h3>
              <p>
                Purchase a life insurance policy with Living Benefits riders included.
                Same price as traditional coverage with more protection.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">2</div>
              <h3>Qualifying Event</h3>
              <p>
                If you're diagnosed with a covered critical illness, chronic condition,
                or suffer a critical injury while your policy is active.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">3</div>
              <h3>File a Claim</h3>
              <p>
                Submit your claim with medical documentation. The insurance company
                reviews and determines the benefit amount.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">4</div>
              <h3>Receive Funds</h3>
              <p>
                Access a portion of your death benefit tax-free. Use it for medical
                bills, lost income, or any purpose you choose.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifying Conditions */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2>Qualifying Conditions</h2>
            <p className="living-benefits-page__section-subtitle">
              Living Benefits can be accessed for these categories of health events.
            </p>
          </div>
          <div className="living-benefits-page__conditions">
            {benefitConditions.map((condition) => (
              <Card key={condition.id} className="living-benefits-page__condition-card">
                <div className={`living-benefits-page__condition-icon living-benefits-page__condition-icon--${condition.category}`}>
                  <Icon
                    icon={
                      condition.category === 'critical-illness' ? 'heart-broken' :
                      condition.category === 'chronic' ? 'time' : 'flash'
                    }
                    size={28}
                  />
                </div>
                <h3>{condition.title}</h3>
                <p>{condition.description}</p>
                <ul className="living-benefits-page__condition-examples">
                  {condition.examples.map((example, index) => (
                    <li key={index}>
                      <Icon icon="tick" size={14} />
                      {example}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Comparison */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <h2>Why Choose Living Benefits?</h2>
            <p className="living-benefits-page__section-subtitle">
              See how Living Benefits compare to traditional life insurance.
            </p>
          </div>
          <div className="living-benefits-page__comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="living-benefits-page__table-old">Traditional Life Insurance</th>
                  <th className="living-benefits-page__table-new">Living Benefits Insurance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Death Benefit</td>
                  <td><Icon icon="tick" /> Yes</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Access While Living</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Critical Illness Protection</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Chronic Illness Coverage</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Critical Injury Benefits</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Tax-Free Benefits</td>
                  <td><Icon icon="tick" /> Death Only</td>
                  <td><Icon icon="tick" className="text-accent" /> Living & Death</td>
                </tr>
                <tr>
                  <td>Extra Cost for Riders</td>
                  <td>N/A</td>
                  <td><Icon icon="tick" className="text-accent" /> $0 Extra</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="living-benefits-page__faq">
            <div className="living-benefits-page__faq-item">
              <h3>How much of my death benefit can I access?</h3>
              <p>
                The amount varies based on your condition's severity and impact on life expectancy.
                Typically, you can access 50-90% of your death benefit. The remaining balance
                continues as a reduced death benefit for your beneficiaries.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Is the money I receive taxable?</h3>
              <p>
                In most cases, Living Benefits are received tax-free. The accelerated death benefit
                is generally treated as an advance of the death benefit, which is typically not
                subject to income tax. Consult a tax professional for your specific situation.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Can I use the money for anything?</h3>
              <p>
                Yes! Unlike health insurance that only pays medical providers, Living Benefits
                funds are yours to use however you need — medical bills, mortgage payments,
                daily expenses, treatments, or even a family vacation.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>What happens if I recover?</h3>
              <p>
                Great news — you keep the money! Living Benefits don't need to be repaid if you
                recover. Your policy continues with a reduced death benefit based on what you
                accessed.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Do I have to be terminally ill?</h3>
              <p>
                No. While terminal illness qualifies, Living Benefits also cover critical illnesses
                (like cancer or heart attack), chronic conditions, and critical injuries — even
                when you're expected to recover.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>How long does it take to receive benefits?</h3>
              <p>
                Once your claim is approved, you typically receive funds within 2-4 weeks.
                The claims process is designed to be as fast as possible given the urgency
                of your situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--dark">
        <div className="container">
          <div className="living-benefits-page__cta">
            <h2>Get Protected Today</h2>
            <p>
              Don't wait until it's too late. Discover how Living Benefits can protect
              your family from financial hardship during life's toughest moments.
            </p>
            <Link to="/contact">
              <Button
                intent="success"
                large
                rightIcon={<Icon icon="arrow-right" />}
              >
                Request Your Free Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LivingBenefitsPage;
